import { JSX, Suspense, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, BrowserRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PersistGate } from "redux-persist/integration/react";
import { LinearProgress, Slide } from '@mui/material';
import { SnackbarProvider } from "notistack";

import { persistor, RootState } from './state/store';
import { SignalRProvider } from "./components/Providers/SignalRProvider";
import UserInfoRefreshListener from "./components/Providers/UserInfoRefreshListener";
import { GlobalModalProvider } from './components/Providers/GlobalModalProvider';
import Header from './components/Header/Header';
import Login from './pages/Login/Login';
import SignedInArrival from './pages/Login/SignedInArrival';
import NotFound from "./pages/NotFound/NotFound";
import { API_BASE_URL } from "./config";
import { TimezoneProvider } from "./components/Providers/TimezoneProvider";
import useShiftBoundarySync from "./components/Hooks/useShiftBoundarySync";
import { UserDto } from "./DTOs/User/UserDto";
import { declaresRequirements, routeArea, satisfiesRouteRequirements } from './utilities/helpers/routeAccess';
import { RouteAreaContext } from './utilities/helpers/routeArea';
import { AreaKey, ViewKey } from './utilities/helpers/pageVocabulary.generated';
import { AppRouteConfig, createAppRoutes } from "./utilities/appRoutes";
import ShiftLoginBootstrapper from "./components/Providers/ShiftLoginBootstrapper";
import GlobalNotificationListener from './components/Providers/GlobalNotificationListener';
import GeofenceRefreshListener from './components/Providers/GeofenceRefreshListener';
import { useTelemetryEnabled } from './components/Hooks/useTelemetryEnabled';
import { useDrillBlastModules } from './components/Hooks/useDrillBlastSettings';
import ErrorScreen from './components/ErrorScreen/ErrorScreen';
import { useSignalRContext } from './components/Contexts/SignalRContext';
import { useApplyTheme } from './components/Hooks/useApplyTheme';
import { useOnlineStatus } from './components/Hooks/useOnlineStatus';
import { resolveAppShellState } from './utilities/helpers/appShellState';
import ConnectionBanner from './components/ConnectionBanner/ConnectionBanner';
import { AccessChangeNotice } from './components/AccessChangeNotice/AccessChangeNotice';
import { AppMuiProvider } from './components/Providers/MuiThemeProvider';
import StatusBanner from './components/StatusBanner/StatusBanner';
import OfflineGuard from './components/OfflineGuard/OfflineGuard';
import { useSyncWorker } from './offline/useSyncWorker'
import { useShiftBands } from './offline/useShiftBands';
import { usePrefetchCriticalPages } from './offline/usePrefetchCriticalPages';

function SlideLeftTransition(props: React.ComponentProps<typeof Slide>) {
  return <Slide {...props} direction="left" />;
}

interface ProtectedRouteProps {
  user: UserDto | null;
  view?: ViewKey;
  alsoOpenTo?: readonly AreaKey[];
  children: JSX.Element;
  isAuthenticated?: boolean;
  adminOnly?: boolean;
  leadAdminOnly?: boolean;
  openToLeadAdmins?: boolean;
}

function ProtectedRoute(
  {
    user,
    view,
    alsoOpenTo,
    children,
    isAuthenticated,
    adminOnly,
    leadAdminOnly,
    openToLeadAdmins
  }: ProtectedRouteProps) {
  const requirements = { view, alsoOpenTo, adminOnly, leadAdminOnly, openToLeadAdmins };

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (declaresRequirements(requirements) && !user) return <LinearProgress />;

  const hasPermission = satisfiesRouteRequirements(requirements, user);

  if (!hasPermission) {
    return <Navigate to="/" />;
  }

  return <RouteAreaContext.Provider value={routeArea(view)}>{children}</RouteAreaContext.Provider>;
}

function generateRoutes(routes: AppRouteConfig[], user: UserDto | null, isAuthenticated: boolean): JSX.Element[] {
  return routes.map((route, i) => {
    const element = route.protected
      ? (
        <ProtectedRoute
          key={route.path}
          user={user}
          view={route.view}
          alsoOpenTo={route.alsoOpenTo}
          isAuthenticated={isAuthenticated}
          adminOnly={route.adminOnly}
          leadAdminOnly={route.leadAdminOnly}
          openToLeadAdmins={route.openToLeadAdmins}
        >
          {route.element ?? <Outlet />}
        </ProtectedRoute>
      )
      : route.element ?? <Outlet />;

    return (
      <Route
        key={route.path || i}
        path={route.path}
        element={element}
      >
        {route.children && generateRoutes(route.children, user, isAuthenticated)}
      </Route>
    );
  });
}

function AppLayout() {
  useShiftBoundarySync();
  useApplyTheme();
  useSyncWorker();
  useShiftBands();
  usePrefetchCriticalPages();
  const { isConnectionLoading } = useSignalRContext();
  const { isAuthenticated, isAuthLoading, error } = useSelector((state: RootState) => state.authReducer);
  const isOnline = useOnlineStatus();
  const telemetryEnabled = useTelemetryEnabled();
  const hasShownContentRef = useRef(false);

  const shellState = resolveAppShellState({// Note: only auth errors show the blocking ErrorScreen; a live-connection problem keeps the page up and is surfaced via the banner below instead.
    isAuthLoading,
    isAuthenticated,
    isConnectionLoading,
    hasAuthError: !!error,
    isOffline: !isOnline,
    hasShownContent: hasShownContentRef.current,
  });
  useEffect(() => {
    if (shellState === "content") hasShownContentRef.current = true;
  }, [shellState]);

  return (
    <AppMuiProvider>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={SlideLeftTransition}
        autoHideDuration={3000}
        dense
        preventDuplicate
      >
        <ConnectionBanner />
        <AccessChangeNotice />
        <GlobalModalProvider>
          <div className="h-screen flex flex-col overflow-hidden bg-theme-bg">
            <Header />
            <main className="flex-1 overflow-y-auto bg-theme-bg transition-colors min-h-0 flex flex-col">
              <StatusBanner />
              <div className="flex-1 min-h-0 flex flex-col">
                {shellState === "loading" ? (
                  <LinearProgress />
                ) : shellState === "error" ? (
                  <ErrorScreen />
                ) : (
                  <>
                    <GlobalNotificationListener />
                    {telemetryEnabled && <GeofenceRefreshListener />}
                    <OfflineGuard>
                      <Suspense fallback={<LinearProgress />}>
                        <Outlet />
                      </Suspense>
                    </OfflineGuard>
                  </>
                )}
              </div>
            </main>
          </div>
        </GlobalModalProvider>
      </SnackbarProvider>
    </AppMuiProvider>
  );
}

// TODO: merge material type & class, buckets + bucket factors, Plant + Plant Buckets, pits and pit stages
function AppRouter({ isAuthenticated, user }: { isAuthenticated: boolean, user: UserDto | null }) {
  const telemetryEnabled = useSelector((state: RootState) => state.authReducer.roleConfig?.telemetryEnabled);
  const drillBlastModules = useDrillBlastModules();
  const routes = useMemo(() => createAppRoutes({ telemetryEnabled, drillBlastModules }), [telemetryEnabled, drillBlastModules]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          isAuthenticated
            ? <SignedInArrival />
            : <Login />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
            <AppLayout />
          </ProtectedRoute>
        }>
          {generateRoutes(routes, user, isAuthenticated)}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

const App: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.authReducer.isAuthenticated);
  const user = useSelector((state: RootState) => state.authReducer.user);

  return (
    <PersistGate loading={<LinearProgress />} persistor={persistor}>
      <TimezoneProvider user={user}>
        <SignalRProvider hubUrl={`${API_BASE_URL}/liveUpdateHub`}>
          <ShiftLoginBootstrapper />
          <UserInfoRefreshListener />
          <AppRouter isAuthenticated={isAuthenticated} user={user} />
        </SignalRProvider>
      </TimezoneProvider>
    </PersistGate>
  );
};

export default App;
